import React, { useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings, MapPin, CheckCircle, AlertCircle } from 'lucide-react';
import { MemberMap } from '@/components/location/MemberMap';
import { GeofenceManager } from '@/components/location/GeofenceManager';
import { useLocation } from '@/contexts/LocationContext';
import { AppStateContext } from '@/contexts/AppStateContext';
import { Role } from '@/types';

function haversineMeters(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371e3;
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(Δφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function timeAgo(timestampMs: number): string {
  const diffMs = Date.now() - timestampMs;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'Active now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.floor(diffMin / 60);
  return `${diffH}h ago`;
}

export const FamilyLocation: React.FC = () => {
  const navigate = useNavigate();
  const { state } = useContext(AppStateContext)!;
  const { memberLocations, geofences, geofenceEvents, checkins, locationPrefs, submitCheckin } =
    useLocation();

  const [permissionDenied, setPermissionDenied] = useState(false);
  const [showGeofenceManager, setShowGeofenceManager] = useState(false);
  const [checkinZone, setCheckinZone] = useState<string | null>(null);
  const [checkinPending, setCheckinPending] = useState(false);

  const currentUser = state.currentUser;
  const isAdmin =
    currentUser?.role === Role.ADMIN || currentUser?.role === Role.OWNER;

  const sharingMembers = useMemo(() => {
    const result: Array<{ userId: string; name: string; location: any }> = [];
    memberLocations.forEach((loc, userId) => {
      const member = state.households
        .flatMap((h) => (h as any).members ?? [])
        .find((m: any) => m.id === userId || m.userId === userId);
      const name = member?.name ?? member?.displayName ?? userId;
      result.push({ userId, name, location: loc });
    });
    return result;
  }, [memberLocations, state.households]);

  // Determine nearby geofence for check-in FAB
  const nearbyFence = useMemo(() => {
    const myLoc = currentUser ? memberLocations.get(currentUser.id) : null;
    if (!myLoc) return null;
    return (
      geofences.find(
        (f) => haversineMeters(myLoc.lat, myLoc.lng, f.lat, f.lng) <= 500
      ) ?? null
    );
  }, [memberLocations, geofences, currentUser]);

  const handleCheckin = async () => {
    if (!nearbyFence) return;
    setCheckinPending(true);
    try {
      await submitCheckin(nearbyFence.name);
      setCheckinZone(nearbyFence.name);
      setTimeout(() => setCheckinZone(null), 3000);
    } finally {
      setCheckinPending(false);
    }
  };

  // Check geolocation permission
  React.useEffect(() => {
    navigator.permissions?.query({ name: 'geolocation' }).then((result) => {
      if (result.state === 'denied') setPermissionDenied(true);
    });
  }, []);

  const recentEvents = [...geofenceEvents].slice(0, 10);
  const recentCheckins = [...checkins].slice(0, 5);

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-2xl bg-brand/10 flex items-center justify-center">
            <MapPin className="w-5 h-5 text-brand" />
          </div>
          <div>
            <h1 className="text-xl font-black text-slate-900 dark:text-white">Family Location</h1>
            <p className="text-xs text-slate-500">{sharingMembers.length} sharing</p>
          </div>
        </div>
        <button
          onClick={() => navigate('/profile/location-settings')}
          className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          title="Location privacy settings"
        >
          <Settings className="w-5 h-5 text-slate-400" />
        </button>
      </div>

      {/* Permission denied banner */}
      {permissionDenied && (
        <div className="flex items-start gap-3 px-4 py-3 bg-rose-50 dark:bg-rose-900/20 border border-rose-100 dark:border-rose-800 rounded-2xl">
          <AlertCircle className="w-5 h-5 text-rose-500 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-bold text-rose-700 dark:text-rose-400">Location access denied</p>
            <p className="text-xs text-rose-600 dark:text-rose-500 mt-0.5">
              Open your browser settings and allow location access to share your position with household members.
            </p>
          </div>
        </div>
      )}

      {/* Map */}
      <MemberMap members={sharingMembers} geofences={geofences} />

      {/* Empty state */}
      {sharingMembers.length === 0 && !permissionDenied && (
        <div className="text-center py-8">
          <MapPin className="w-12 h-12 text-slate-200 dark:text-slate-700 mx-auto mb-3" />
          <p className="text-sm font-semibold text-slate-400">No one is sharing their location</p>
          <p className="text-xs text-slate-400 mt-1">
            Enable location sharing in your privacy settings to appear on the map.
          </p>
          <button
            onClick={() => navigate('/profile/location-settings')}
            className="mt-4 px-4 py-2 bg-brand text-white rounded-xl text-sm font-bold hover:bg-brand/90 transition-colors"
          >
            Enable sharing
          </button>
        </div>
      )}

      {/* Member cards */}
      {sharingMembers.length > 0 && (
        <div className="space-y-2">
          <h2 className="text-xs font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">
            Members
          </h2>
          {sharingMembers.map(({ userId, name, location }) => {
            const now = Date.now();
            const isActive = now - location.timestamp.toMillis() < 5 * 60 * 1000;
            const homeFence = geofences.find((f) => f.name.toLowerCase() === 'home');
            const distFromHome = homeFence
              ? Math.round(haversineMeters(location.lat, location.lng, homeFence.lat, homeFence.lng))
              : null;

            return (
              <div
                key={userId}
                className="flex items-center gap-4 px-4 py-3 bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-2xl"
              >
                <div className="relative">
                  <div className="w-10 h-10 rounded-full bg-brand/10 flex items-center justify-center text-brand font-black text-sm">
                    {name.charAt(0).toUpperCase()}
                  </div>
                  {isActive && (
                    <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-white dark:border-slate-800" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-slate-800 dark:text-slate-100 truncate">{name}</p>
                  <p className="text-xs text-slate-500">{timeAgo(location.timestamp.toMillis())}</p>
                </div>
                <div className="text-right shrink-0">
                  {location.batteryLevel !== null && (
                    <p className="text-xs text-slate-400">{Math.round((location.batteryLevel ?? 0) * 100)}%</p>
                  )}
                  {distFromHome !== null && (
                    <p className="text-xs text-slate-400">
                      {distFromHome < 1000
                        ? `${distFromHome}m from Home`
                        : `${(distFromHome / 1000).toFixed(1)}km from Home`}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Geofence event feed */}
      {recentEvents.length > 0 && (
        <div className="space-y-2">
          <h2 className="text-xs font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">
            Recent Activity
          </h2>
          {recentEvents.map((ev) => (
            <div
              key={ev.id}
              className="flex items-center gap-3 px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-xl"
            >
              <span className={`text-xs ${ev.type === 'arrive' ? 'text-green-500' : 'text-blue-400'}`}>
                {ev.type === 'arrive' ? '→' : '←'}
              </span>
              <p className="text-xs text-slate-600 dark:text-slate-400">
                <span className="font-semibold text-slate-800 dark:text-slate-200">{ev.memberName}</span>{' '}
                {ev.type === 'arrive' ? 'arrived at' : 'left'}{' '}
                <span className="font-semibold">{ev.zoneName}</span>
              </p>
              <span className="ml-auto text-xs text-slate-400 shrink-0">
                {timeAgo(ev.timestamp.toMillis())}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Recent check-ins */}
      {recentCheckins.length > 0 && (
        <div className="space-y-2">
          <h2 className="text-xs font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">
            Check-ins
          </h2>
          {recentCheckins.map((ci) => (
            <div
              key={ci.id}
              className="flex items-center gap-3 px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-xl"
            >
              <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0" />
              <p className="text-xs text-slate-600 dark:text-slate-400">
                <span className="font-semibold text-slate-800 dark:text-slate-200">{ci.userName}</span>{' '}
                checked in at <span className="font-semibold">{ci.zoneName}</span>
              </p>
              <span className="ml-auto text-xs text-slate-400 shrink-0">
                {timeAgo(ci.timestamp.toMillis())}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Admin: Geofence Manager */}
      {isAdmin && (
        <div className="space-y-3">
          <button
            onClick={() => setShowGeofenceManager((v) => !v)}
            className="w-full flex items-center justify-between px-4 py-3 bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-2xl hover:border-brand/50 transition-colors"
          >
            <span className="text-sm font-bold text-slate-700 dark:text-slate-300">Manage Zones</span>
            <span className="text-xs text-brand">{showGeofenceManager ? 'Hide' : 'Show'}</span>
          </button>
          {showGeofenceManager && <GeofenceManager />}
        </div>
      )}

      {/* Check-in FAB */}
      {nearbyFence && (
        <div className="fixed bottom-8 right-6 z-50">
          {checkinZone ? (
            <div className="flex items-center gap-2 px-4 py-3 bg-emerald-600 text-white rounded-2xl shadow-lg font-bold text-sm">
              <CheckCircle className="w-4 h-4" /> Checked in at {checkinZone}!
            </div>
          ) : (
            <button
              onClick={handleCheckin}
              disabled={checkinPending}
              className="flex items-center gap-2 px-5 py-3 bg-brand text-white rounded-2xl shadow-lg font-bold text-sm hover:bg-brand/90 active:scale-95 transition-all disabled:opacity-70"
            >
              <CheckCircle className="w-4 h-4" />
              Check in at {nearbyFence.name}
            </button>
          )}
        </div>
      )}
    </div>
  );
};
